import { FC, useRef, useState } from 'react';

import HotTable, { HotColumn } from '@handsontable/react';
import type Handsontable from 'handsontable';
import { keyBy, keys } from 'lodash';
import * as XLSX from 'xlsx';
import FileSaver from 'file-saver';
import { IList } from 'core/API/listApi';
import { IRetailer } from 'shared/types';
import { useTranslation } from 'helpers/languages/translations';

import Grid from '../Grid';
import { listValidator, numberValidator } from '../Validators';
import { AutoCompleteRenderer } from './helpers/customRenderers';
import { requireTitle } from './helpers/utils';
import { MTopSearchByTypeHeader, translateHeaders } from './helpers/HeadersList';
import ErrorModal from '../ErrorModal';
import { CellValue } from 'pages/ListManagement/types';

export const SEARCH_TYPES = ['Default', 'Organic', 'Paid'];

export type TRowWithErrors = {
    Retailer: CellValue;
    Type: CellValue;
    Top: CellValue;
    Errors?: string;
};

const isEmptyRow = (row: (string | number | boolean | null)[]) =>
    row.every((value) => value === '' || value === null || value === undefined);

export const hasValidRetailerRules = (data: (string | number | boolean | null)[][], rowIndex: number) => {
    const row = data[rowIndex];

    if (!row || isEmptyRow(row)) return true;

    const retailer = row[0];
    const type = row[1];
    if (!retailer || !type) return false;

    const retailerRows = data.filter((candidate) => candidate[0] === retailer);
    const defaultCount = retailerRows.filter((candidate) => candidate[1] === 'Default').length;
    const hasDuplicateType = retailerRows.filter((candidate) => candidate[1] === type).length > 1;

    return defaultCount === 1 && !hasDuplicateType;
};

const rowRulesValidator = function (this: Handsontable.CellProperties, _value: unknown, cb: (valid: boolean) => void) {
    cb(hasValidRetailerRules(this.instance.getData() as (string | number | boolean | null)[][], this.row));
};

export const MTopSearchByType: FC<{
    list: IList;
    toolId: number;
    retailersData: IRetailer[];
}> = ({ list, toolId, retailersData }) => {
    const translatedHeaders = translateHeaders(MTopSearchByTypeHeader);
    const retailersByName = keyBy(retailersData, 'name');
    const retailersById = keyBy(retailersData, 'id');
    const retailerNames = keys(retailersByName).sort();
    const translate = useTranslation();

    const hotRef = useRef<React.ElementRef<typeof HotTable> | null>(null);
    const hasErrorsRef = useRef<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [rowsToDownload, setRowsToDownload] = useState<TRowWithErrors[]>([]);

    const translateExcelDataKeys = (data: TRowWithErrors[]) => {
        return data.map((row) => {
            const translatedRow: { [key: string]: CellValue | string } = {};
            Object.keys(row).forEach((key, index) => {
                const translatedKey =
                    index === 3 ? translate('list.top_search_by_type.error_file.header.errors') : translatedHeaders[index];
                translatedRow[translatedKey as string] = row[key as keyof TRowWithErrors] ?? '';
            });
            return translatedRow;
        });
    };

    const downloadFile = (data: TRowWithErrors[], fileName: string) => {
        const worksheet = XLSX.utils.json_to_sheet(translateExcelDataKeys(data));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        FileSaver.saveAs(blob, `${fileName}.xlsx`);
    };

    const beforeValidate = () => {
        const hot = hotRef?.current?.hotInstance;
        const data = (hot?.getData() || []) as (string | number | boolean | null)[][];
        const nonEmptyRows = data.filter((row) => row?.some((value) => value !== null && value !== ''));

        let errorsFound = false;
        const rowsWithErrors: TRowWithErrors[] = [];

        const missingRetailerTranslation = translate('list.top_search_by_type.error.missing_field.retailer') as string;
        const missingTypeTranslation = translate('list.top_search_by_type.error.missing_field.type') as string;
        const missingTopTranslation = translate('list.top_search_by_type.error.missing_field.top') as string;
        const invalidRetailerTranslation = translate('list.top_search_by_type.error.invalid_retailer') as string;
        const invalidTypeTranslation = translate('list.top_search_by_type.error.invalid_type') as string;
        const invalidTopTranslation = translate('list.top_search_by_type.error.invalid_top') as string;
        const missingDefaultTranslation = translate('list.top_search_by_type.error.missing_default') as string;
        const multipleDefaultTranslation = translate('list.top_search_by_type.error.multiple_default') as string;

        const firstSeenByRetailerType = new Map<string, number>();
        const defaultCountByRetailer = new Map<string, number>();

        nonEmptyRows.forEach((row) => {
            const retailer = row[0];
            const type = row[1];
            if (type === 'Default') {
                defaultCountByRetailer.set(`${retailer}`, (defaultCountByRetailer.get(`${retailer}`) ?? 0) + 1);
            }
        });

        for (let index = 0; index < nonEmptyRows.length; index++) {
            const row = nonEmptyRows[index];
            const retailer = row[0];
            const type = row[1];
            const top = row[2];
            const errors: string[] = [];

            if (!retailer) {
                errors.push(missingRetailerTranslation);
            } else if (!retailerNames.includes(`${retailer}`)) {
                errors.push(invalidRetailerTranslation);
            }

            if (!type) {
                errors.push(missingTypeTranslation);
            } else if (!SEARCH_TYPES.includes(`${type}`)) {
                errors.push(invalidTypeTranslation);
            }

            if (top === null || top === '' || top === undefined) {
                errors.push(missingTopTranslation);
            } else if (!Number.isFinite(Number(top)) || Number(top) < 1) {
                errors.push(invalidTopTranslation);
            }

            if (retailer && type) {
                const key = `${retailer}::${type}`;
                const firstSeenIndex = firstSeenByRetailerType.get(key);
                if (typeof firstSeenIndex === 'number') {
                    errors.push(
                        translate('list.top_search_by_type.error.duplicate_type', {
                            type,
                            line: firstSeenIndex + 2,
                        }) as string,
                    );
                } else {
                    firstSeenByRetailerType.set(key, index);
                }

                const defaultCount = defaultCountByRetailer.get(`${retailer}`) ?? 0;
                if (defaultCount === 0) {
                    errors.push(missingDefaultTranslation);
                } else if (defaultCount > 1 && type === 'Default') {
                    errors.push(multipleDefaultTranslation);
                }
            }

            if (errors.length) errorsFound = true;

            rowsWithErrors.push({ Retailer: retailer, Type: type, Top: top, Errors: errors.join('; ') });
        }

        hasErrorsRef.current = errorsFound;
        if (errorsFound) {
            setRowsToDownload(rowsWithErrors);
            setIsModalOpen(true);
        }
    };

    const afterValidate = (listToValidate: IList) => ({
        ...listToValidate,
        data: listToValidate.data.map((row) => {
            const newRow = [...row];
            newRow[0] = retailersByName[`${newRow[0]}`]?.id ?? '';
            if (newRow[2] !== '' && newRow[2] !== null && newRow[2] !== undefined) {
                newRow[2] = Number(newRow[2]);
            }
            return newRow;
        }),
    });

    const formatList = (listToFormat: IList) => ({
        ...listToFormat,
        data: listToFormat.data.map((row) => {
            const newRow = [...row];
            newRow[0] = retailersById[`${newRow[0]}`]?.name ?? '';
            if (newRow[2] !== '' && newRow[2] !== null && newRow[2] !== undefined) {
                newRow[2] = Number(newRow[2]);
            }
            return newRow;
        }),
    });

    return (
        <>
            <Grid
                paramHotRef={hotRef}
                toolId={toolId}
                headers={MTopSearchByTypeHeader}
                list={formatList(list)}
                beforeValidate={beforeValidate}
                afterValidate={afterValidate}
                hasErrors={hasErrorsRef}
            >
                <HotColumn
                    title={requireTitle(translatedHeaders[0])}
                    validator={function (this: Handsontable.CellProperties, value, cb) {
                        listValidator(retailerNames)(`${value ?? ''}`, (valid) => {
                            if (!valid) cb(false);
                            else rowRulesValidator.call(this, `${value ?? ''}`, cb);
                        });
                    }}
                    source={retailerNames}
                    type="autocomplete"
                >
                    <AutoCompleteRenderer list={retailerNames} hot-renderer />
                </HotColumn>

                <HotColumn
                    title={requireTitle(translatedHeaders[1])}
                    validator={function (this: Handsontable.CellProperties, value, cb) {
                        listValidator(SEARCH_TYPES)(`${value ?? ''}`, (valid) => {
                            if (!valid) cb(false);
                            else rowRulesValidator.call(this, `${value ?? ''}`, cb);
                        });
                    }}
                    source={SEARCH_TYPES}
                    type="dropdown"
                >
                    <AutoCompleteRenderer list={SEARCH_TYPES} hot-renderer />
                </HotColumn>

                <HotColumn
                    type="text"
                    validator={function (this: Handsontable.CellProperties, value, cb) {
                        numberValidator(false, { min: 1 }, true)(`${value ?? ''}`, (valid) => {
                            if (!valid) cb(false);
                            else rowRulesValidator.call(this, `${value ?? ''}`, cb);
                        });
                    }}
                    title={requireTitle(translatedHeaders[2])}
                />
            </Grid>
            <ErrorModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setRowsToDownload([]);
                }}
                onSubmit={() => {
                    downloadFile(rowsToDownload, 'top_search_by_type_list_errors');
                }}
                isLoading={false}
            />
        </>
    );
};